<template>
  <v-row no-gutters>
    <patient-card
      :patient="patient"
      v-if="patient"
      @cardClick="selectPatientFromClick"
      :hideDetails="context !== 'records'"
      :recordHeader="context === 'records'"
    />
    <v-card
      v-else
      width="100%"
      class="pa-2 mt-2 vetalfa-card-primary-border vetalfa-pointer"
      flat
      @click="openPatientsAndOwnersSearch"
    >
      <v-row class="ma-0 py-2 pl-2 pr-6" align="end" justify="start">
        <v-icon
          x-small
          style="position: absolute; top: 42%; right: 8px; translateY(-50%)"
          color="primary"
        >
          {{ mdiChevronRight }}
        </v-icon>
        <v-img
          max-width="28px"
          :src="animals.getAnimalIcon(1)"
          class="mr-1 mt-1"
        ></v-img>
        <v-img
          max-width="28px"
          :src="animals.getAnimalIcon(2)"
          class="mr-8"
        ></v-img>
        <v-col class="ma-0 pa-0">
          <p
            class="subtitle-2 grey--text text--darken-2 ma-0 pa-0 mb-1"
            v-html="_getLabel('CLICK_TO_SELECT_PATIENT')"
          ></p>
          <p class="subtitle-2 grey--text text--darken-2 ma-0 pa-0">
            {{ _getLabel("CLICK_TO_SELECT_PATIENT_DETAILS") }}
          </p>
        </v-col>
      </v-row>
    </v-card>
    <search
      :items="patientsAndOwners"
      :placeholder="_getLabel('PATIENTS_OWNER_SEARCH')"
      @fetch-data="filterPatientsAndOwners"
      :loadingTrigger="searchLoadingTrigger"
      :searchTriggerModel="searchModelTrigger"
      :filters="searchFilters"
      :noDataPlaceholder="noPatientsFoundPlaceholder"
      :title="_getLabel('SEARCH_PATIENTS_AND_OWNERS')"
      ads="patients"
    >
      <template v-slot:item="{ item }">
        <patient-card
          :patient="item"
          v-if="typeof item.species === 'number'"
          @cardClick="confirmPatientSelectionFromClick"
        />
        <v-card
          flat
          v-else
          @click="selectOwnerFromSearch(item.id)"
          height="60px"
          width="100%"
          class="px-2 py-2 mt-2 vetalfa-card-grey-border vetalfa-pointer"
        >
          <v-icon
            x-small
            style="position: absolute; top: 40%; right: 8px; translateY(-50%)"
          >
            {{ mdiChevronRight }}
          </v-icon>
          <v-row class="ma-0 pa-0 vetalfa-row" align="center">
            <v-avatar size="32" class="mr-3" color="secondary">
              <v-icon size="20" color="primary">{{ _icon("user") }}</v-icon>
            </v-avatar>
            <v-col :cols="10" class="ma-0 pa-0">
              <v-row class="ma-0 pa-0" align="start" justify="start">
                <p class="ma-0 pa-0 subtitle-2">{{ item.name }}</p>
              </v-row>
              <v-row class="ma-0 pa-0" align="start" justify="start">
                <p class="ma-0 pa-0 items-description-label">
                  {{ _getLabel("TUTOR") }}
                </p>
              </v-row>
            </v-col>
          </v-row>
        </v-card>
      </template>
      <template v-slot:appendItem>
        <v-btn
          @click="algorithmMode = 3"
          rounded
          color="primary"
          class="text-none ma-0 pa-0 px-10 mb-4"
        >
          <v-icon small class="mr-1">{{ mdiPlus }}</v-icon>
          {{ _getLabel("ADD_OWNER") }}
        </v-btn>
      </template>
    </search>
    <vetalfa-dialog
      v-on:dialogClose="patientModelTrigger = false"
      :bottom="_xsOnly()"
      :toggleModel="patientModelTrigger"
      :fullscreen="false"
      max-width="700px"
      :height="dialogHeight"
      background-color="secondary"
      :smallHeader="false"
      :featureMode="false"
      :dialogHeader="{
        title: dialogHeaderTitle,
      }"
    >
      <template v-slot:header v-if="!algorithmMode">
        <v-tabs
          active-class="primary--text font-weight-bold"
          background-color="secondary"
          slider-color="primary"
          grow
          v-model="tabModel"
        >
          <v-tab class="text-none"> {{ _getLabel("ABOUT") }} </v-tab>
          <v-tab class="text-none" v-if="showSecondTab">
            {{ _getLabel(owner ? "PATIENTS" : "PRESCRIPTIONS") }}
          </v-tab>
        </v-tabs>
        <v-divider></v-divider>
      </template>

      <v-tabs-items
        v-model="tabModel"
        v-if="patient && !algorithmMode && !owner"
        class="secondary"
      >
        <v-tab-item class="secondary">
          <vetalfa-section-title
            :prop="_getLabel('PATIENT_SELECTED').replace('{1}', '')"
          ></vetalfa-section-title>
          <vetalfa-section-item
            :prop="`<b>${_getLabel('NAME')}:</b> ${patient.name}`"
          ></vetalfa-section-item>
          <vetalfa-section-item
            :index="1"
            :prop="`<b>${_getLabel(
              'SPECIES'
            )}:</b> ${animals.getAnimalShortDescription(patient.species)}`"
          ></vetalfa-section-item>
          <vetalfa-section-item
            :index="2"
            :prop="`<b>${_getLabel('BREED')}:</b> ${patient.breed}`"
          ></vetalfa-section-item>
          <vetalfa-section-item
            :index="3"
            :prop="`<b>${_getLabel('SEX')}:</b> ${animals.getSexDescription(
              patient.sex
            )}`"
          ></vetalfa-section-item>
          <vetalfa-section-item
            :index="4"
            v-if="patient.birthday"
            :prop="`<b>${_getLabel('BIRTHDAY')}:</b> ${patient.birthday}`"
          ></vetalfa-section-item>
          <vetalfa-section-item
            :index="5"
            :prop="`<b>${_getLabel('AGE')}:</b> ${
              patient.birthday
                ? _calculateAgeFromStringBirthday(patient.birthday)
                : _formatNumber(patient.age, _getLabel('YEARS'))
            }`"
          ></vetalfa-section-item>
          <vetalfa-section-item
            :index="5"
            :prop="`<b>${_getLabel('WEIGHT')}:</b> ${patientWeightDescription}`"
          ></vetalfa-section-item>
          <vetalfa-section-item
            :index="6"
            v-if="patientExtraProps.bodyScore"
            :prop="`<b>${_getLabel('BODY_SCORE')}:</b> ${patientExtraProps.bodyScore}`"
          ></vetalfa-section-item>
          <vetalfa-section-item
            :index="7"
            v-if="patientExtraProps.color"
            :prop="`<b>${_getLabel('COLOR')}:</b> ${patientExtraProps.color}`"
          ></vetalfa-section-item>
          <vetalfa-section-item
            :index="8"
            v-if="patientExtraProps.neutered"
            :prop="`<b>${_getLabel('NEUTERED')}:</b> ${patientExtraProps.neutered}`"
          ></vetalfa-section-item>
          <vetalfa-section-item
            :index="9"
            v-if="patientExtraProps.microchip"
            :prop="`<b>${_getLabel('MICROCHIP')}:</b> ${patientExtraProps.microchip}`"
          ></vetalfa-section-item>
          <vetalfa-section-item
            :index="10"
            v-if="patientExtraProps.insurance"
            :prop="`<b>${_getLabel('INSURANCE_PROVIDER')}:</b> ${patientExtraProps.insurance}`"
          ></vetalfa-section-item>

          <v-row class="ma-0 pa-0 my-5 px-3" justify="center">
            <v-btn
              @click="algorithmMode = 2"
              rounded
              small
              outlined
              color="primary"
              class="text-none px-7"
            >
              <v-icon small class="mr-2">{{ mdiPencilOutline }}</v-icon>
              {{ _getLabel("CLICK_TO_EDIT") }}
            </v-btn>
          </v-row>
          <vetalfa-section-title
            :prop="_getLabel('TUTOR')"
          ></vetalfa-section-title>
          <v-card
            flat
            v-if="patient.ownerId"
            @click="selectOwnerFromSearch(patient.ownerId)"
            height="60px"
            class="px-2 py-2 my-3 mx-3 vetalfa-card-grey-border vetalfa-pointer"
          >
            <v-icon
              x-small
              style="position: absolute; top: 40%; right: 8px; translateY(-50%)"
            >
              {{ mdiChevronRight }}
            </v-icon>
            <v-row class="ma-0 pa-0 vetalfa-row" align="center">
              <v-avatar size="32" class="mr-3" color="secondary">
                <v-icon size="20" color="primary">{{ _icon("user") }}</v-icon>
              </v-avatar>
              <v-col :cols="10" class="ma-0 pa-0">
                <v-row class="ma-0 pa-0" align="start" justify="start">
                  <p class="ma-0 pa-0 subtitle-2">
                    {{ patient.ownerName }}
                  </p>
                </v-row>
                <v-row class="ma-0 pa-0" align="start" justify="start">
                  <p class="ma-0 pa-0 items-description-label">
                    {{ _getLabel("CLICK_TO_EDIT") }}
                  </p>
                </v-row>
              </v-col>
            </v-row>
          </v-card>
        </v-tab-item>
        <v-tab-item class="secondary" v-if="showSecondTab">
          <v-row
            no-gutters
            v-if="prescriptions && !prescriptions.length"
            justify="center"
            class="mt-8 px-4"
          >
            <p class="text-center">
              {{ _getLabel("NO_PRESCRIPTION_ADDED_FOR_THIS_PATIENT") }}
            </p>
          </v-row>
          <v-row no-gutters v-else-if="prescriptions">
            <v-col
              class="ma-0 px-4 py-2"
              cols="12"
              v-for="prescription in prescriptions"
              :key="prescription.id"
            >
              <v-card
                flat
                class="vetalfa-card-grey-border vetalfa-pointer pa-3"
                @click="openPrescriptionFromPatient(prescription)"
              >
                <v-icon
                  x-small
                  style="position: absolute; top: 42%; right: 8px; translateY(-50%)"
                >
                  {{ mdiChevronRight }}
                </v-icon>

                <v-row class="ma-0 pa-0" align="start" justify="start">
                  <v-col class="ma-0 pa-0" cols="12">
                    <p class="ma-0 pa-0 subtitle-2">
                      <span class="font-weight-bold">
                        {{ `${_getLabel("DATE")}: ` }}
                      </span>
                      {{
                        prescription.shareDate ||
                        _getDateFromTimestamp(prescription.lastUpdate)
                      }}
                    </p>
                  </v-col>
                  <v-col class="ma-0 pa-0" cols="12">
                    <p class="ma-0 pa-0 subtitle-2">
                      <span class="font-weight-bold">
                        {{ `${_getLabel("TAB_GUIDES")}: ` }}
                      </span>
                      {{ prescription.pages.length }}
                    </p>
                  </v-col>
                  <v-col class="ma-0 pa-0" cols="12">
                    <p class="ma-0 pa-0 subtitle-2">
                      <span class="font-weight-bold">
                        {{ `${_getLabel("PRESCRIPTION_ITEM_TOTAL")}: ` }}
                      </span>
                      {{ getPrescriptionItemCount(prescription) }}
                    </p>
                  </v-col>
                  <v-col class="ma-0 pa-0" cols="12">
                    <p class="ma-0 pa-0 subtitle-2">
                      <span class="font-weight-bold">
                        {{ `${_getLabel("STATUS")}: ` }}
                      </span>
                      {{ _getLabel(prescription.shareDate ? "SENT" : "DRAFT") }}
                    </p>
                  </v-col>
                </v-row>
              </v-card>
            </v-col>
            <v-col class="ma-0 px-4 py-2" cols="12">
              <v-row no-gutters class="vetalfa-row" justify="center">
                <v-btn
                  @click="getPatientPrescriptions"
                  text
                  color="primary"
                  class="text-none px-7 text-decoration-underline"
                >
                  <v-icon small class="mr-2">{{ _icon("magnify") }}</v-icon>
                  {{ _getLabel("SEE_MORE") }}
                </v-btn>
              </v-row>
            </v-col>
          </v-row>
        </v-tab-item>
      </v-tabs-items>
      <v-tabs-items
        v-model="tabModel"
        v-else-if="owner && !algorithmMode"
        class="secondary"
      >
        <v-tab-item class="secondary">
          <vetalfa-section-title prop="Dados do Tutor"></vetalfa-section-title>
          <div v-for="(ownerProp, index) in ownerProps" :key="ownerProp.label">
            <vetalfa-section-item
              :index="index"
              v-if="ownerProp.value"
              :prop="ownerProp.label"
            ></vetalfa-section-item>
          </div>

          <v-row class="ma-0 pa-0 my-5 px-3" justify="center">
            <v-btn
              @click="algorithmMode = 4"
              rounded
              small
              outlined
              class="text-none px-7 primary--text"
            >
              <v-icon small class="mr-1" color="primary">{{
                mdiPencilOutline
              }}</v-icon>
              {{ _getLabel("CLICK_TO_EDIT") }}
            </v-btn>
          </v-row>
        </v-tab-item>
        <v-tab-item class="secondary">
          <v-row
            no-gutters
            v-if="!owner.patients.length"
            justify="center"
            class="mt-8"
          >
            <p class="text-center">
              {{ _getLabel("NO_PATIENT_ADDED_FOR_THIS_OWNER") }}
            </p>
          </v-row>
          <vetalfa-section-title
            :prop="animals.getAnimalPlural(1)"
            v-if="getPatientBySpecies(owner.patients, 1).length > 0"
          />
          <v-row
            class="ma-0 pa-0 mt-2 px-3"
            justify="center"
            v-for="ownerPatient in getPatientBySpecies(owner.patients, 1)"
            :key="ownerPatient.id"
          >
            <patient-card
              :patient="ownerPatient"
              @cardClick="confirmPatientSelectionFromClick"
            />
          </v-row>
          <vetalfa-section-title
            :prop="animals.getAnimalPlural(2)"
            v-if="getPatientBySpecies(owner.patients, 2).length > 0"
          />
          <v-row
            class="ma-0 pa-0 mt-2 px-3"
            justify="center"
            v-for="ownerPatient in getPatientBySpecies(owner.patients, 2)"
            :key="ownerPatient.id"
          >
            <patient-card
              :patient="ownerPatient"
              @cardClick="confirmPatientSelectionFromClick"
            />
          </v-row>
        </v-tab-item>
      </v-tabs-items>

      <algorithms
        :headerHeightOffset="50"
        class="my-5"
        v-if="algorithmMode"
        :dialogContainer="true"
      ></algorithms>
      <v-row v-if="algorithmMode" no-gutters style="height: 100px"></v-row>
      <template v-slot:actions>
        <v-row
          no-gutters
          justify="center"
          class="py-5"
          v-if="owner && !algorithmMode"
        >
          <v-btn
            @click="algorithmMode = 1"
            rounded
            color="primary"
            class="text-none px-7"
          >
            <v-icon small class="mr-2">{{ mdiPlus }}</v-icon>
            {{ _getLabel("ADD_NEW_PATIENT") }}
          </v-btn>
        </v-row>
        <v-row
          no-gutters
          justify="space-around"
          class="pt-5 px-2"
          v-if="patient && !algorithmMode && !owner"
        >
          <v-btn
            @click="openPatientsAndOwnersSearch"
            rounded
            color="primary"
            class="text-none px-5 mb-5"
            outlined
          >
            {{ _getLabel("SEARCH_OTHER_PATIENT") }}
          </v-btn>
          <v-btn
            @click="patientModelTrigger = false"
            rounded
            color="primary"
            class="text-none px-5 mb-5"
          >
            {{ getContinueButtonActionName() }}
          </v-btn>
        </v-row>
      </template>
    </vetalfa-dialog>
  </v-row>
</template>

<style scoped>
</style>

<script>
import {
  mdiChevronRight,
  mdiPencilOutline,
  mdiPlus,
  mdiSwapHorizontal,
} from "@mdi/js";
import animals from "@/constants/animals.enum.js";
import Search from "@/components/Search.vue";
import Algorithms from "@/algorithms/Algorithms.vue";
import { mapMutations, mapGetters, mapActions } from "vuex";
import PatientCard from "@/components/PatientCard.vue";
export default {
  name: "Patients",
  components: {
    search: Search,
    algorithms: Algorithms,
    "patient-card": PatientCard,
  },
  data() {
    return {
      searchLoadingTrigger: false,
      mdiChevronRight,
      mdiPencilOutline,
      mdiPlus,
      mdiSwapHorizontal,
      animals,
      searchModelTrigger: false,
      patientModelTrigger: false,
      tabModel: 0,
      algorithmMode: null,
      searchFilters: [],
      dialogHeight: null,
      searchText: null,
    };
  },
  props: {
    context: {
      type: String,
      default: null,
      required: false,
    },
  },
  computed: {
    ...mapGetters("patients", [
      "patient",
      "patientsAndOwners",
      "owner",
      "prescriptions",
    ]),
    ...mapGetters("algorithms", ["flowEndTrigger"]),
    noPatientsFoundPlaceholder() {
      let text = this._getLabel("PATIENTS_NO_DATA");
      if (this.searchText && this.searchText.length < 3) {
        text += ` ${this._getLabel("PATIENTS_TYPE_3_LETTERS")}`;
      }
      return text;
    },
    dialogHeaderTitle() {
      let label = { 1: "PATIENTS", 2: "PATIENTS", 3: "OWNERS", 4: "OWNERS" }[
        this.algorithmMode
      ];
      if (!label) {
        label = this.owner ? "OWNERS" : "PATIENTS";
      }
      return this._getLabel(label);
    },
    showSecondTab() {
      if (this.owner) return true;
      return this.context !== "records";
    },
    patientWeightDescription() {
      if (!this.patient) return "—";
      const weight = this._formatNumber(this.patient.weight, "kg");
      // O peso inicial não tem data histórica. Só a exibimos quando o backend
      // informou explicitamente quando o peso foi atualizado.
      if (!Number.isFinite(this.patient.weightUpdatedAt)) return weight;
      return `${weight} (${this._getLabel("DATE")}: ${this._getDateFromTimestamp(
        this.patient.weightUpdatedAt
      )})`;
    },
    patientExtraProps() {
      if (!this.patient) return {};
      const p = this.patient;
      const bodyScoreLabels = [
        "BODY_SCORE_UNDEFINED",
        "BODY_SCORE_VERY_THIN",
        "BODY_SCORE_THIN",
        "BODY_SCORE_IDEAL",
        "BODY_SCORE_OVERWEIGHT",
        "BODY_SCORE_OBESE",
      ];
      const bodyScore =
        typeof p.bodyScore === "number" && p.bodyScore >= 0
          ? this._getLabel(bodyScoreLabels[p.bodyScore])
          : null;
      const neutered =
        p.neutered === 1
          ? this._getLabel("YES")
          : p.neutered === 0
          ? this._getLabel("NO")
          : p.neutered === 2
          ? this._getLabel("NEUTERED_UNCERTAIN")
          : null;
      const microchip =
        p.hasMicrochip === "yes" && p.microchip
          ? p.microchip
          : p.hasMicrochip === "yes"
          ? this._getLabel("YES")
          : p.hasMicrochip === "no"
          ? this._getLabel("NO")
          : p.hasMicrochip === "unsure"
          ? this._getLabel("UNCERTAIN")
          : p.microchip || null;
      const insurance =
        p.insurance && p.insurance.provider
          ? p.insurance.provider
          : p.insuranceProvider || null;
      return {
        bodyScore,
        color: p.color || null,
        neutered,
        microchip,
        insurance,
      };
    },
    ownerProps() {
      if (!this.owner) return [];
      const {
        name,
        cpf,
        email,
        phone,
        address,
        addressZipCode,
        addressStreet,
        addressNumber,
        addressComplement,
        addressNeighborhood,
        addressCity,
        addressState,
      } = this.owner;
      const formattedAddress = this._formatBrazilianAddress({
        zipCode: addressZipCode,
        street: addressStreet,
        number: addressNumber,
        complement: addressComplement,
        neighborhood: addressNeighborhood,
        city: addressCity,
        state: addressState,
      });
      return [
        {
          label: `<b>${this._getLabel("NAME")}:</b> ${name}`,
          value: name,
        },
        {
          label: `<b>${this._getLabel("CPF")}:</b> ${cpf}`,
          value: cpf,
        },
        {
          label: `<b>${this._getLabel("EMAIL_INPUT_LABEL")}:</b> ${email}`,
          value: email,
        },
        {
          label: `<b>${this._getLabel("PHONE")}:</b> ${phone}`,
          value: phone,
        },
        {
          label: `<b>${this._getLabel("ADDRESS")}:</b> ${formattedAddress}`,
          value: formattedAddress,
        },
        {
          label: `<b>${this._getLabel("OWNER_ADDRESS_LEGACY")}:</b> ${address}`,
          value: !formattedAddress && address ? address : null,
        },
      ];
    },
  },
  methods: {
    ...mapActions("patients", [
      "openPatientAddition",
      "openPatientEdition",
      "openOwnerAddition",
      "openOwnerEdition",
      "getPatientsAndOwners",
      "savePatient",
      "saveOwner",
      "selectPatient",
      "selectOwner",
      "getPatientPrescriptions",
    ]),
    ...mapActions("general", ["showMessage"]),
    ...mapActions("prescriptions", [
      "openPrescriptionFromPatientAction",
      "changePrescriptionsPatientAction",
    ]),
    ...mapMutations("general", ["setConfirmModel"]),
    getContinueButtonActionName() {
      if (this.context === "records") {
        return this._getLabel("CONTINUE_WITH_RECORDS");
      }
      return this._getLabel(
        {
          prescriptions: "CONTINUE_WITH_PRESCRIPTIONS",
          "clinical-handouts": "CONTINUE_WITH_HANDOUTS",
          "patient-record": "CONTINUE_WITH_RECORDS",
        }[this.$route.name]
      );
    },
    openPrescriptionFromPatient(prescription) {
      this.openPrescriptionFromPatientAction(prescription);
      this.$nextTick(() => {
        this.patientModelTrigger = false;
      });
    },
    getPrescriptionItemCount(prescription) {
      let count = 0;
      prescription.pages.forEach((page) => {
        count += page.length;
      });
      return count;
    },
    getPatientBySpecies(array, species) {
      return array.filter((p) => p.species === species);
    },
    openPatientsAndOwnersSearch() {
      this.searchModelTrigger = this._getTimestamp();
      this.$nextTick(() => {
        this.patientModelTrigger = false;
      });
    },
    async filterPatientsAndOwners({ search }) {
      this.searchText = search;
      await this.getPatientsAndOwners(search);
      this.searchLoadingTrigger = this._getTimestamp();
    },
    async confirmPatientSelectionFromClick(patient) {
      this.searchModelTrigger = false;
      if (this.context === "records") {
        return this.selectPatientFromClick(patient, true);
      }
      const hasPrescriptionChange =
        this.$route.name !== "prescriptions" ||
        (await this.changePrescriptionsPatientAction());
      if (hasPrescriptionChange) {
        this.selectPatientFromClick(patient, true);
      } else {
        this.$nextTick(() => {
          this.setConfirmModel({
            text: this._getMessage("ASK_FOR_PATIENT_SELECTION_OR_VIEW"),
            callback: () => {
              this.selectPatientFromClick(patient, false);
            },
            cancelAction: () => {
              this.selectPatientFromClick(patient, true);
            },
            confirmText: this._getLabel("SELECT_AND_VIEW"),
            closeText: this._getLabel("SELECT_ONLY"),
          });
        });
      }
    },
    selectPatientFromClick(patient, closeModal = false) {
      this.selectPatient({ ...patient });
      this.searchModelTrigger = false;
      if (!closeModal) {
        this.$nextTick(() => {
          this.patientModelTrigger = this._getTimestamp();
        });
      } else {
        this.patientModelTrigger = false;
      }
    },
    async selectOwnerFromSearch(ownerId) {
      await this.selectOwner(ownerId);
      this.searchModelTrigger = false;
      this.$nextTick(() => {
        this.patientModelTrigger = this._getTimestamp();
      });
    },
  },
  watch: {
    async flowEndTrigger() {
      if (this.patientModelTrigger) {
        const modes = {
          1: async () => {
            this.tabModel = 1;
            await this.savePatient(true);
          },
          2: async () => {
            await this.savePatient();
          },
          3: async () => {
            await this.saveOwner(true);
          },
          4: async () => {
            await this.saveOwner();
          },
        };
        try {
          await modes[this.algorithmMode]();
        } catch {
          this.patientModelTrigger = false;
        }
        this.$nextTick(() => {
          this.algorithmMode = null;
        });
      }
    },
    async tabModel(val) {
      this.$nextTick(async () => {
        if (
          val === 1 &&
          this.patient &&
          !this.owner &&
          !this.prescriptions &&
          this.showSecondTab
        ) {
          await this.getPatientPrescriptions();
        }
      });
    },
    algorithmMode(val) {
      const modes = {
        1: this.openPatientAddition,
        2: this.openPatientEdition,
        3: () => {
          this.searchModelTrigger = false;
          this.openOwnerAddition();
          this.$nextTick(() => {
            this.patientModelTrigger = this._getTimestamp();
          });
        },
        4: this.openOwnerEdition,
      };
      if (val) {
        modes[val]();
      }
    },
    patientModelTrigger(val) {
      this.tabModel = 0;
      if (!val && this.algorithmMode) {
        if (this.algorithmMode !== 3) {
          this.patientModelTrigger = this._getTimestamp();
        } else {
          this.patientModelTrigger = false;
          this.searchModelTrigger = this._getTimestamp();
        }
        this.algorithmMode = null;
      }
    },
  },
  mounted() {
    this.filterPatientsAndOwners({});
    this.dialogHeight = this._getDialogHeight(this._isIos() ? 0.9 : 0.95);
  },
};
</script>
